import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bookmark, Quote, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PreviewGate } from "@/components/access/PreviewGate";
import { useAuth } from "@/context/AuthContext";
import { fetchLesson } from "@/lib/lumenApi";

function LessonLoadingState() {
  return (
    <div className="lumen-page">
      <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
        <div className="lumen-frame max-w-xl p-10 text-center">
          <p className="lumen-kicker" data-testid="lesson-loading-kicker">
            Leçon initiatique
          </p>
          <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="lesson-loading-title">
            La traversée se déploie.
          </h1>
          <p className="lumen-copy mt-4" data-testid="lesson-loading-copy">
            La chambre prend forme derrière le seuil.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LessonPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [lessonPayload, setLessonPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let active = true;

    fetchLesson(slug)
      .then((data) => {
        if (active) {
          setLessonPayload(data);
        }
      })
      .catch(() => {
        if (active) {
          setErrorMessage("Cette leçon reste voilée pour l’instant.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [slug, user]);

  const lesson = lessonPayload?.lesson;
  const access = lessonPayload?.access;
  const isPreview = access?.visibility === "preview";

  if (loading || !lesson) {
    if (!loading && errorMessage) {
      return (
        <div className="lumen-page" id="top">
          <div className="lumen-shell flex min-h-screen items-center justify-center px-6 py-24">
            <div className="lumen-frame max-w-xl p-10 text-center">
              <p className="lumen-kicker" data-testid="lesson-error-kicker">
                Leçon initiatique
              </p>
              <h1 className="lumen-title mt-4 text-4xl text-[var(--lumen-ivory)]" data-testid="lesson-error-title">
                Cette chambre n’est pas encore visible.
              </h1>
              <p className="lumen-copy mt-4" data-testid="lesson-error-copy">
                {errorMessage}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return <LessonLoadingState />;
  }

  const lessonPageClassName = `lumen-page lesson-page lesson-page--${lesson.slug}`;

  return (
    <div className={lessonPageClassName} id="top">
      <div className="lumen-shell pb-20">
        <header className="lumen-header fade-rise" data-testid="lesson-header">
          <div className="flex flex-wrap items-center gap-3" data-testid="lesson-meta-row">
            <span className="lumen-chip" data-testid="lesson-step-label">
              {lesson.step_label}
            </span>
            <span className="lumen-chip" data-testid="lesson-index-label">
              {lesson.lesson_index}
            </span>
            <span className={`lumen-status-pill ${isPreview ? "preview" : "open"}`} data-testid="lesson-access-state">
              {isPreview ? "Aperçu" : "Chambre ouverte"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-testid="lesson-header-actions">
            <Button asChild className="lumen-outline-button" data-testid="lesson-home-button" variant="outline">
              <Link to="/">
                <ArrowLeft />
                Revenir au seuil
              </Link>
            </Button>
            <div className="lumen-bookmark" data-testid="lesson-bookmark-icon">
              <Bookmark className="h-4 w-4 text-[var(--lumen-gold)]" />
            </div>
          </div>
        </header>

        <main className="lesson-page-main space-y-0" data-testid="lesson-main">
          <section className="lumen-scroll-stage stage-manifest lesson-entry-section grid gap-8 xl:grid-cols-[1.05fr_0.95fr]" data-testid="lesson-hero-section">
            <div className="space-y-7 fade-rise delay-1">
              <div className="space-y-4">
                <p className="lumen-kicker" data-testid="lesson-hero-kicker">
                  {lesson.entry_label}
                </p>
                <h1 className="lumen-title max-w-4xl text-5xl sm:text-6xl lg:text-7xl" data-testid="lesson-title">
                  {lesson.title}
                </h1>
                <p className="text-2xl leading-snug text-[var(--lumen-gold)]" data-testid="lesson-subtitle">
                  {lesson.subtitle}
                </p>
              </div>

              <p className="lumen-copy max-w-2xl text-base md:text-lg" data-testid="lesson-intro">
                {lesson.intro}
              </p>

              <p className="lesson-entry-bridge lumen-copy" data-testid="lesson-entry-bridge">
                {lesson.entry_bridge}
              </p>

              <div className="lumen-vow-strip" data-testid="lesson-vow-strip">
                {lesson.vows.map((vow, index) => (
                  <span className="lumen-vow" key={vow} data-testid={`lesson-vow-${index + 1}`}>
                    {vow}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-4" data-testid="lesson-hero-actions">
                {isPreview ? (
                  <>
                    <Button asChild className="lumen-gold-button" data-testid="lesson-preview-threshold-button">
                      <a href="#preview-gate-anchor">
                        Voir le seuil de profondeur
                        <ArrowRight />
                      </a>
                    </Button>
                    <Button asChild className="lumen-outline-button" data-testid="lesson-preview-enter-button" variant="outline">
                      <Link to="/entrer">
                        Entrer dans LUMEN
                        <Sparkles />
                      </Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild className="lumen-gold-button" data-testid="lesson-primary-action-button">
                      <a href="#methode">
                        {lesson.primary_action_label}
                        <ArrowRight />
                      </a>
                    </Button>
                    <Button asChild className="lumen-outline-button" data-testid="lesson-secondary-action-button" variant="outline">
                      <a href="#reflexion">
                        {lesson.secondary_action_label}
                        <Sparkles />
                      </a>
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="fade-rise delay-2" data-testid="lesson-image-column">
              <div className="lumen-image-frame lesson-frame portal-frame">
                <div className="lumen-image-cartouche" data-testid="lesson-image-cartouche">
                  <p className="lumen-kicker" data-testid="lesson-image-cartouche-kicker">
                    {lesson.image_cartouche_title}
                  </p>
                  <p className="lumen-image-cartouche-text" data-testid="lesson-image-cartouche-text">
                    {lesson.image_cartouche_text}
                  </p>
                </div>
                <img
                  src={lesson.hero_image_url}
                  alt={lesson.hero_image_alt}
                  className="lumen-image lesson-hero-image lesson-portal-image"
                  data-testid="lesson-hero-image"
                />
                <div className="lumen-floating-note lesson-note" data-testid="lesson-side-note-card">
                  <p className="lumen-kicker" data-testid="lesson-side-note-title">
                    {lesson.side_note_title}
                  </p>
                  <p className="text-sm leading-7 text-[var(--lumen-ivory)]" data-testid="lesson-side-note-body">
                    {lesson.side_note_body}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="lumen-scroll-stage stage-doctrine lesson-formulation-section space-y-7" data-testid="lesson-key-panels-section">
            <div className="lesson-sequence-intro" data-testid="lesson-formulation-intro">
              <p className="lumen-kicker" data-testid="lesson-formulation-kicker">
                {lesson.formulation_label}
              </p>
              <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="lesson-formulation-title">
                {lesson.formulation_title}
              </h2>
              <p className="lumen-copy" data-testid="lesson-formulation-copy">
                {lesson.formulation_copy}
              </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-[0.92fr_1.08fr]" data-testid="lesson-formulation-grid">
              <Card className="lumen-card value-major accent-halo h-full" data-testid="lesson-key-idea-card">
                <CardHeader className="space-y-4">
                  <div className="lumen-panel-topline" data-testid="lesson-key-idea-topline">
                    <p className="lumen-panel-kicker" data-testid="lesson-key-idea-panel-kicker">
                      {lesson.key_idea_panel_label}
                    </p>
                    <span className="lumen-plus">+</span>
                  </div>
                  <p className="lumen-kicker" data-testid="lesson-key-idea-kicker">
                    {lesson.key_idea_title}
                  </p>
                  <CardTitle className="lumen-card-title text-3xl" data-testid="lesson-key-idea-title">
                    {lesson.key_idea_headline}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="lumen-copy text-base md:text-lg" data-testid="lesson-key-idea-body">
                    {lesson.key_idea_body}
                  </p>
                </CardContent>
              </Card>

              <Card className="lumen-card value-doctrine accent-mist h-full" data-testid="lesson-quote-card">
                <CardHeader className="space-y-4">
                  <div className="lumen-panel-topline" data-testid="lesson-quote-topline">
                    <p className="lumen-panel-kicker" data-testid="lesson-quote-panel-kicker">
                      {lesson.quote_panel_label}
                    </p>
                    <span className="lumen-plus">+</span>
                  </div>
                  <div className="flex items-center gap-3 text-[var(--lumen-gold)]" data-testid="lesson-quote-label-row">
                    <Quote className="h-5 w-5" />
                    <p className="lumen-kicker" data-testid="lesson-quote-label">
                      {lesson.quote_label}
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="lumen-title text-3xl leading-tight text-[var(--lumen-ivory)] sm:text-4xl" data-testid="lesson-quote-text">
                    {lesson.quote_text}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {isPreview ? (
            <div id="preview-gate-anchor" data-testid="lesson-preview-anchor">
              <PreviewGate access={access} />
            </div>
          ) : (
            <>
              <section id="methode" className="lumen-scroll-stage stage-doctrine lesson-clarification-section space-y-8" data-testid="lesson-method-section">
                <div className="space-y-3 fade-rise delay-2">
                  <p className="lumen-kicker" data-testid="lesson-method-kicker">
                    {lesson.method_kicker}
                  </p>
                  <div className="lumen-section-heading" data-testid="lesson-method-heading-row">
                    <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="lesson-method-title">
                      {lesson.method_label}
                    </h2>
                    <span className="lumen-plus" data-testid="lesson-method-plus">
                      +
                    </span>
                  </div>
                  <Separator className="lumen-separator max-w-3xl" data-testid="lesson-method-separator" />
                  <p className="lesson-method-support lumen-copy" data-testid="lesson-method-support">
                    {lesson.method_support}
                  </p>
                </div>

                <div className="grid gap-5 xl:grid-cols-4 lumen-subgrid cluster-doctrine" data-testid="lesson-steps-grid">
                  {lesson.steps.map((step) => (
                    <Card className={`lumen-card lumen-step-card value-doctrine accent-${step.accent} h-full`} key={step.number} data-testid={`lesson-step-card-${step.number}`}>
                      <CardHeader className="space-y-4">
                        <div className="lumen-panel-topline" data-testid={`lesson-step-topline-${step.number}`}>
                          <p className="lumen-panel-kicker" data-testid={`lesson-step-panel-kicker-${step.number}`}>
                            {lesson.step_panel_prefix} {step.number}
                          </p>
                          <span className="lumen-plus">+</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="lumen-step-number" data-testid={`lesson-step-number-${step.number}`}>
                            {step.number}
                          </span>
                          <span className="lumen-symbol" data-testid={`lesson-step-symbol-${step.number}`}>
                            {step.symbol}
                          </span>
                        </div>
                        <CardTitle className="lumen-card-title text-3xl" data-testid={`lesson-step-title-${step.number}`}>
                          {step.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <p className="lumen-copy" data-testid={`lesson-step-description-${step.number}`}>
                          {step.description}
                        </p>
                        <div className="lumen-action-box lesson-step-action-box value-fragment" data-testid={`lesson-step-action-box-${step.number}`}>
                          <p className="lumen-kicker" data-testid={`lesson-step-action-title-${step.number}`}>
                            {step.action_title === "Action" ? "Geste d’intégration" : step.action_title}
                          </p>
                          <p className="lumen-copy text-sm" data-testid={`lesson-step-action-body-${step.number}`}>
                            {step.action_body}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="lumen-scroll-stage stage-manifest stage-pivot lumen-stage-panel value-major lesson-revelation-stage" data-testid="lesson-revelation-stage">
                <img
                  src={lesson.hero_image_url}
                  alt={lesson.hero_image_alt}
                  className="lumen-stage-image lesson-stage-image"
                  data-testid="lesson-revelation-image"
                />
                <div className="lumen-stage-overlay" data-testid="lesson-revelation-overlay" />
                <div className="lumen-stage-axis" data-testid="lesson-revelation-axis" />
                <div className="lumen-stage-portal" data-testid="lesson-revelation-portal" />
                <div className="lumen-stage-copy" data-testid="lesson-revelation-copy">
                  <p className="lumen-kicker" data-testid="lesson-revelation-kicker">
                    {lesson.revelation_label}
                  </p>
                  <h3 className="lumen-title text-4xl sm:text-5xl lg:text-6xl" data-testid="lesson-revelation-title">
                    {lesson.quote_text}
                  </h3>
                  <p className="lumen-copy max-w-2xl text-base md:text-lg" data-testid="lesson-revelation-text">
                    {lesson.revelation_copy}
                  </p>
                </div>
                <div className="lumen-stage-plaque plaque-top-left" data-testid="lesson-revelation-plaque">
                  <p className="lumen-panel-kicker">{lesson.revelation_plaque_title}</p>
                  <p className="lumen-image-cartouche-text">{lesson.revelation_plaque_text}</p>
                </div>
              </section>

              <section className="lumen-scroll-stage stage-doctrine lesson-fixation-section space-y-7" data-testid="lesson-example-section">
                <div className="lesson-sequence-intro lesson-sequence-intro-narrow" data-testid="lesson-fixation-intro">
                  <p className="lumen-kicker" data-testid="lesson-fixation-kicker">
                    {lesson.fixation_label}
                  </p>
                  <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="lesson-fixation-title">
                    {lesson.fixation_title}
                  </h2>
                  <p className="lumen-copy" data-testid="lesson-fixation-copy">
                    {lesson.fixation_copy}
                  </p>
                </div>

                <div className="grid gap-5 xl:grid-cols-[1.08fr_0.92fr]" data-testid="lesson-fixation-grid">
                  <Card className="lumen-card value-doctrine accent-ember h-full" data-testid="lesson-example-card">
                    <CardHeader className="space-y-4">
                      <div className="lumen-panel-topline" data-testid="lesson-example-topline">
                        <p className="lumen-panel-kicker" data-testid="lesson-example-panel-kicker">
                          Mise en réalité
                        </p>
                        <span className="lumen-plus">+</span>
                      </div>
                      <p className="lumen-kicker" data-testid="lesson-example-kicker">
                        {lesson.example_title}
                      </p>
                      <CardTitle className="lumen-card-title text-3xl" data-testid="lesson-example-title">
                        {lesson.example_headline}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <p className="lumen-copy" data-testid="lesson-example-intro">
                        {lesson.example_intro}
                      </p>

                      <div className="grid gap-4 md:grid-cols-2 lumen-subgrid cluster-fragment" data-testid="lesson-example-nodes-grid">
                        {lesson.example_nodes.map((node, index) => (
                          <div className="lumen-node value-fragment" key={node.label} data-testid={`lesson-example-node-${index + 1}`}>
                            <p className="lumen-kicker" data-testid={`lesson-example-node-label-${index + 1}`}>
                              {node.label}
                            </p>
                            <p className="lumen-copy text-sm" data-testid={`lesson-example-node-text-${index + 1}`}>
                              {node.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="lumen-card value-major accent-halo h-full" data-testid="lesson-takeaway-card">
                    <CardHeader className="space-y-4">
                      <div className="lumen-panel-topline" data-testid="lesson-takeaway-topline">
                        <p className="lumen-panel-kicker" data-testid="lesson-takeaway-panel-kicker">
                          {lesson.takeaway_panel_label}
                        </p>
                        <span className="lumen-plus">+</span>
                      </div>
                      <p className="lumen-kicker" data-testid="lesson-takeaway-kicker">
                        {lesson.takeaway_title}
                      </p>
                      <CardTitle className="lumen-card-title text-3xl" data-testid="lesson-takeaway-title">
                        {lesson.takeaway_headline}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl leading-relaxed text-[var(--lumen-ivory)]" data-testid="lesson-takeaway-text">
                        {lesson.takeaway_text}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </section>

              <section id="reflexion" className="lumen-scroll-stage stage-fragment lesson-silence-section space-y-8" data-testid="lesson-reflection-section">
                <div className="space-y-3 fade-rise delay-3">
                  <p className="lumen-kicker" data-testid="lesson-reflection-kicker">
                    {lesson.reflection_kicker}
                  </p>
                  <div className="lumen-section-heading" data-testid="lesson-reflection-heading-row">
                    <h2 className="lumen-title text-3xl sm:text-4xl" data-testid="lesson-reflection-title">
                      {lesson.reflection_title}
                    </h2>
                    <span className="lumen-plus" data-testid="lesson-reflection-plus">
                      +
                    </span>
                  </div>
                  <p className="lesson-reflection-support lumen-copy" data-testid="lesson-reflection-support">
                    {lesson.reflection_support}
                  </p>
                </div>

                <div className="grid gap-5 lg:grid-cols-3 lumen-subgrid cluster-fragment" data-testid="lesson-reflection-grid">
                  {lesson.reflection_prompts.map((prompt, index) => (
                    <Card className="lumen-card value-fragment h-full" key={prompt.question} data-testid={`lesson-reflection-card-${index + 1}`}>
                      <CardContent className="space-y-5 p-6">
                        <div className="lumen-panel-topline" data-testid={`lesson-reflection-topline-${index + 1}`}>
                          <p className="lumen-panel-kicker" data-testid={`lesson-reflection-panel-kicker-${index + 1}`}>
                            Question {String(index + 1).padStart(2, "0")}
                          </p>
                          <span className="lumen-plus">+</span>
                        </div>
                        <span className="lumen-symbol" data-testid={`lesson-reflection-icon-${index + 1}`}>
                          {prompt.icon}
                        </span>
                        <p className="text-xl leading-relaxed text-[var(--lumen-ivory)]" data-testid={`lesson-reflection-question-${index + 1}`}>
                          {prompt.question}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="lumen-scroll-stage stage-threshold stage-access stage-pivot lumen-frame value-access lesson-closing-shell space-y-5 p-8 fade-rise delay-3" data-testid="lesson-closing-section">
                <p className="lumen-kicker" data-testid="lesson-closing-kicker">
                  {lesson.closing_kicker}
                </p>
                <p className="lesson-closing-label lumen-copy" data-testid="lesson-closing-label">
                  {lesson.closing_cta_label}
                </p>
                <p className="lumen-title text-3xl leading-tight sm:text-4xl" data-testid="lesson-closing-text">
                  {lesson.closing_cta_text}
                </p>
                <p className="lesson-closing-support lumen-copy" data-testid="lesson-closing-support">
                  {lesson.closing_support}
                </p>
                <div className="flex flex-wrap gap-4" data-testid="lesson-closing-actions">
                  <Button asChild className="lumen-gold-button threshold-action-button threshold-return-button" data-testid="lesson-closing-home-button">
                    <Link to="/">
                      {lesson.closing_primary_action_label}
                      <ArrowLeft />
                    </Link>
                  </Button>
                  <Button asChild className="lumen-outline-button threshold-action-button threshold-reorient-button" data-testid="lesson-closing-top-button" variant="outline">
                    <a href="#top">
                      {lesson.closing_secondary_action_label}
                      <ArrowRight />
                    </a>
                  </Button>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}